import { useState } from 'react';
import { Grid, GridItem, Box, Text, useColorModeValue } from '@chakra-ui/react';
import { Rock, User, Milestone, ActionItem, Issue, RockNote } from '../../types';
import { RockCardDetailed } from './RockCardDetailed';

interface RockWithUsers extends Rock {
  owner?: User;
  collaboratorUsers?: User[];
}

interface RocksCardViewProps {
  rocks: RockWithUsers[];
  users: User[];
  currentUserId?: string;
  onRockClick: (rockId: string) => void;
  // Action handlers
  onUpdateRock: (rockId: string, data: any) => Promise<void>;
  onDeleteRock: (rockId: string) => Promise<void>;
  onAchieveRock: (rockId: string) => Promise<void>;
  onDropRock: (rockId: string) => Promise<void>;
  onEvaluateRock: (rockId: string) => Promise<any>;
  onAddIssue: (rockId: string, name: string, description: string, ownerId: string) => Promise<void>;
  onResolveIssue: any; // Adapter needed, typed flexibly for now
  // Data loaders
  loadMilestonesForRock: (rockId: string) => Promise<Milestone[]>;
  loadActionItemsForRock: (rockId: string) => Promise<ActionItem[]>;
  loadIssuesForRock: (rockId: string) => Promise<Issue[]>;
  saveRockUpdate: (rockId: string, note: string, currentUserId: string) => Promise<void>;
  loadPreviousNotes: (rockId: string) => Promise<RockNote[]>;
  // Sub-item handlers
  addMilestone: (rockId: string, task: string, dueDate: string, sortOrder: number) => Promise<void>;
  toggleMilestone: (milestoneId: string, complete: boolean) => Promise<void>;
  addActionItem: (rockId: string, task: string, assigneeId: string | null, dueDate: string) => Promise<void>;
  toggleActionItem: (actionItemId: string, complete: boolean) => Promise<void>;
  // AI
  rockAiScores: Record<string, number>;
}

/**
 * Rocks Card View Component
 * Displays rocks in a grid layout using RockCardDetailed
 */
export function RocksCardView({
  rocks,
  users,
  currentUserId,
  onUpdateRock,
  onDeleteRock,
  onAchieveRock,
  onDropRock,
  onEvaluateRock,
  onAddIssue,
  loadMilestonesForRock,
  loadActionItemsForRock,
  loadIssuesForRock,
  saveRockUpdate,
  loadPreviousNotes,
  addMilestone,
  toggleMilestone,
  addActionItem,
  toggleActionItem,
  rockAiScores,
}: RocksCardViewProps) {
  const [expandedRockId, setExpandedRockId] = useState<string | null>(null);

  const emptyBg = useColorModeValue('white', 'gray.700');
  const emptyBorder = useColorModeValue('gray.200', 'gray.600');
  const emptyText = useColorModeValue('gray.500', 'gray.400');
  const emptyTextLight = useColorModeValue('gray.400', 'gray.500');

  if (rocks.length === 0) {
    return (
      <Box textAlign="center" py={12} bg={emptyBg} borderRadius="lg" border="1px" borderColor={emptyBorder}>
        <Text color={emptyText} fontSize="lg" mb={2}>
          No rocks found
        </Text>
        <Text color={emptyTextLight} fontSize="sm" mt={1}>
          Go to All Rocks to create your first 90-day goal
        </Text>
      </Box>
    );
  }

  const handleToggleExpand = (rockId: string) => {
    setExpandedRockId((prev) => (prev === rockId ? null : rockId));
  };

  return (
    <Grid templateColumns="repeat(auto-fill, minmax(400px, 1fr))" gap={4} alignItems="start">
      {rocks.map((rock) => (
        <GridItem key={rock.id} colSpan={expandedRockId === rock.id ? 2 : 1} rowSpan={expandedRockId === rock.id ? 2 : 1}>
          <RockCardDetailed
            rock={rock}
            users={users}
            isExpanded={expandedRockId === rock.id}
            onToggleExpand={() => handleToggleExpand(rock.id)}
            onEdit={() => {}} // TODO: Wire up edit modal
            onDelete={() => onDeleteRock(rock.id)}
            onAchieve={() => onAchieveRock(rock.id)}
            onDrop={() => onDropRock(rock.id)}
            onEvaluate={() => onEvaluateRock(rock.id)}
            onStatusChange={(status) => onUpdateRock(rock.id, { status })}
            onIssueClick={(rockId) => {
              // TODO: Open add issue modal
              console.log('Add issue clicked', rockId);
            }}
            // Data loaders & handlers
            loadMilestonesForRock={loadMilestonesForRock}
            loadActionItemsForRock={loadActionItemsForRock}
            loadIssuesForRock={loadIssuesForRock}
            saveRockUpdate={saveRockUpdate}
            loadPreviousNotes={loadPreviousNotes}
            addMilestone={addMilestone}
            toggleMilestone={toggleMilestone}
            addActionItem={addActionItem}
            toggleActionItem={toggleActionItem}
            currentUserId={currentUserId}
            aiScore={rockAiScores[rock.id]}
          />
        </GridItem>
      ))}
    </Grid>
  );
}
